import * as React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import { isLoggedIn } from "./lib/auth";

import ScrollToTop from "./components/ScrollToTop";
import CookieConsent from "./components/cookies/CookieConsent";

// Pages vitrine
import ServicesPages from "./pages/ServicesPage";
import TarifsPage from "./pages/TarifsPage";
import EnvoiePage from "./pages/EnvoiePage";
import CriterePage from "./pages/CriterePage";
import CookiePage from "./pages/CookiePage";
import ConditionsGeneralPage from "./pages/ConditionsGeneral";
import MentionsLegalPage from "./pages/MentionsLegal";

// Auth
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import ResetPassword from "./pages/Auth/ResetPassword";

// Recap
import RecapPage from "./pages/RecapPage";

// Admin
import AdminPage from "./pages/AdminPage";
import AdminProducts from "./components/admin/Products";
import AdminTransactions from "./components/admin/Transactions";
import Verification from "./components/admin/Verification";
import AdminUsers from "./components/admin/Users";
import AdminLogs from "./components/admin/Logs"; // ⬅️ NEW
import AdminProspects from "./components/admin/Prospects";
import AdminCollects from "./components/admin/Collects";
import CollectLanding from "./pages/CollectLanding";
import CollectPublicPage from "./pages/CollectPublicPage";
import CollectCookiePage from "./pages/CollectCookiePage";

// Compte
import MyAccountPage from "./pages/MyAccountPage";
import AccountTransactions from "./components/account/AccountTransactions";
import AccountProfile from "./components/account/AccountProfile";

// Protection
import ProtectedRoute from "./components/ProtectedRoute";

// i18n
import { I18nProvider } from "./i18n/I18nProvider";

// Auth overlay
import { AuthOverlayProvider } from "./context/AuthOverlayContext";
import AuthOverlayHost from "./components/auth/AuthOverlayHost";

// Pricing data provider (existant)
import { PricingDataProvider } from "./context/PricingDataContext";

// 🔹 Analytics
import useAnalytics from "./lib/useAnalytics";
import usePresenceAnalytics from "./lib/usePresenceAnalytics";

/* ====== Déclarations globales pour TS (cache bootstrap) ====== */
declare global {
  interface Window {
    __PRICELIST_BOOT__?: {
      brands: any[];
      models: any[];
      ts: number;
      ttlMs: number;
    };
    __PRICELIST_PREFETCHING__?: boolean;
  }
}

/* ====== Utilitaire de préchargement (sans contexte) ====== */
function useTarifsPrefetch() {
  const cooldownMs = 60 * 1000; // 1 min
  const lastPrefetchRef = React.useRef<number>(0);

  const prefetch = React.useCallback(async () => {
    const now = Date.now();

    const boot = window.__PRICELIST_BOOT__;
    if (boot && now - (boot.ts || 0) < (boot.ttlMs || cooldownMs)) return;

    if (now - lastPrefetchRef.current < cooldownMs) return;
    if (window.__PRICELIST_PREFETCHING__) return;

    window.__PRICELIST_PREFETCHING__ = true;
    lastPrefetchRef.current = now;

    try {
      const [brandsRes, modelsRes] = await Promise.all([
        fetch("/brands", { credentials: "include" }),
        fetch("/models", { credentials: "include" }),
      ]);

      if (!brandsRes.ok || !modelsRes.ok) return;

      const [brands, models] = await Promise.all([brandsRes.json(), modelsRes.json()]);

      window.__PRICELIST_BOOT__ = {
        brands: Array.isArray(brands) ? brands : [],
        models: Array.isArray(models) ? models : [],
        ts: Date.now(),
        ttlMs: cooldownMs,
      };
    } catch {
      // silencieux
    } finally {
      window.__PRICELIST_PREFETCHING__ = false;
    }
  }, [cooldownMs]);

  return prefetch;
}

/* ====== Composant qui écoute les survols/focus vers /tarifs ====== */
function PrefetchOnTarifsHover() {
  const prefetch = useTarifsPrefetch();

  React.useEffect(() => {
    const handler = (e: MouseEvent | FocusEvent) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link) return;

      const hrefAttr = link.getAttribute("href") || "";
      let path = hrefAttr;

      try {
        if (hrefAttr.startsWith("http")) {
          const url = new URL(hrefAttr);
          path = url.pathname;
        }
      } catch {
        /* ignore */
      }

      if (typeof path === "string" && path.includes("/tarifs")) {
        prefetch();
      }
    };

    document.addEventListener("mouseover", handler, true);
    document.addEventListener("focusin", handler, true);
    return () => {
      document.removeEventListener("mouseover", handler, true);
      document.removeEventListener("focusin", handler, true);
    };
  }, [prefetch]);

  return null;
}

function App() {
  // ✅ Analytics
  useAnalytics();          // page_view à chaque navigation
  usePresenceAnalytics();  // heartbeat user_engagement toutes les 15s
  const isCollectHost = typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('collect.');

  return (
    <I18nProvider>
      <PricingDataProvider>
        <AuthOverlayProvider>
          {/* Prefetch des prix dès survol/focus d’un lien /tarifs */}
          <PrefetchOnTarifsHover />

          <ScrollToTop behavior="auto" />
          <CookieConsent />

          <Routes>
            {isCollectHost && <Route path="/" element={<CollectLanding />} />}
            {isCollectHost && <Route path="/cookies" element={<CollectCookiePage />} />}
            {isCollectHost && <Route path="/:slug" element={<CollectPublicPage />} />}
            {!isCollectHost && <Route path="/collect" element={<CollectLanding />} />}
            {!isCollectHost && <Route path="/collect/cookies" element={<CollectCookiePage />} />}
            {!isCollectHost && <Route path="/collect/:slug" element={<CollectPublicPage />} />}
            {/* Vitrine */}
            <Route path="/services" element={<ServicesPages />} />
            <Route path="/tarifs" element={<TarifsPage />} />
            <Route path="/envoi-securise" element={<EnvoiePage />} />
            <Route path="/criteres-rachat" element={<CriterePage />} />
            {!isCollectHost && <Route path="/cookies" element={<CookiePage />} />}
            <Route path="/conditions-generales-rachat" element={<ConditionsGeneralPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalPage />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Recap PUBLIC */}
            <Route path="/recap" element={<RecapPage />} />

            {/* Mon compte */}
            <Route
              path="/mes-envois"
              element={
                isLoggedIn() ? (
                  <ProtectedRoute roles={["vendor"]}>
                    <MyAccountPage />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/login" replace state={{ from: "/mes-envois" }} />
                )
              }
            >
              <Route index element={<AccountTransactions />} />
              <Route path="compte" element={<AccountProfile />} />
            </Route>

            {/* Admin */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["admin", "superadmin"]}>
                  <AdminPage />
                </ProtectedRoute>
              }
            >
              {/* 👉 Quand on arrive sur /admin, on redirige vers /admin/transactions */}
              <Route index element={<Navigate to="transactions" replace />} />

              <Route path="products" element={<AdminProducts />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="transactions/:id/verify" element={<Verification />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="logs" element={<AdminLogs />} /> {/* ⬅️ NEW */}
              <Route path="prospects" element={<AdminProspects />} />
              <Route path="collect" element={<AdminCollects />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to={isCollectHost ? "/" : "/services"} replace />} />
          </Routes>

          <AuthOverlayHost />
        </AuthOverlayProvider>
      </PricingDataProvider>
    </I18nProvider>
  );
}

export default App;
